import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  ClientInvoice,
  ClientInvoiceListRow,
  ClientInvoiceLog,
  ClientInvoicePaymentLink,
  ProductInDoc
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { normalizeDateRangePayload } from 'src/app/shared/utils/date-filter.util';

export interface InvoiceProductDto {
  product_id: number;
  quan: number;
  price: number;
}

export interface CreateClientInvoiceDto {
  date?: Date;
  order_id?: number;
  client_id?: number;
  org_id?: number;
  org_bank_account_id?: number;
  comment?: string;
  products?: InvoiceProductDto[];
}

export interface SaveClientInvoiceDto {
  id: number;
  date?: Date;
  client_id?: number;
  org_id?: number;
  org_bank_account_id?: number;
  comment?: string;
  products?: InvoiceProductDto[];
}

export interface SearchClientInvoiceDto {
  id?: number;
  date_from?: Date;
  date_to?: Date;
  client_id?: number;
  order_id?: number;
  user_id?: number;
  org_id?: number;
  org_bank_account_id?: number;
  payment_status?: 'UNPAID' | 'PARTIALLY_PAID' | 'FULLY_PAID';
  shipment_status?: 'UNSHIPPED' | 'PARTIALLY_SHIPPED' | 'FULLY_SHIPPED';
  search_query?: string;
  limit?: number;
  offset?: number;
}

export type ClientInvoicePrintType = 'article' | 'id';

export interface CreateClientInvoicePaymentLinkDto {
  amount: number;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientInvoiceService {
  constructor(private http: HttpClient) {}

  create(payload: CreateClientInvoiceDto) {
    return this.http
      .post<ClientInvoice>(`${environment.BACKEND}/doc/client-invoice/create`, payload)
      .pipe(map((invoice) => this.normalizeInvoice(invoice)));
  }

  save(payload: SaveClientInvoiceDto) {
    return this.http
      .put<ClientInvoice>(`${environment.BACKEND}/doc/client-invoice/save`, payload)
      .pipe(map((invoice) => this.normalizeInvoice(invoice)));
  }

  search(payload: SearchClientInvoiceDto) {
    const normalizedPayload = normalizeDateRangePayload(payload);
    return this.http
      .post<ClientInvoiceListRow[]>(
        `${environment.BACKEND}/doc/client-invoice/search`,
        normalizedPayload
      )
      .pipe(map((rows) => rows.map((row) => this.normalizeListRow(row))));
  }

  getList() {
    return this.http
      .get<ClientInvoiceListRow[]>(`${environment.BACKEND}/doc/client-invoice/list`)
      .pipe(map((rows) => rows.map((row) => this.normalizeListRow(row))));
  }

  getOne(id: number) {
    return this.http
      .get<ClientInvoice>(`${environment.BACKEND}/doc/client-invoice/${id}`)
      .pipe(map((invoice) => this.normalizeInvoice(invoice)));
  }

  getLogs(id: number) {
    return this.http
      .get<ClientInvoiceLog[]>(`${environment.BACKEND}/doc/client-invoice/${id}/logs`)
      .pipe(
        map((logs) =>
          logs.map((log) => ({
            ...log,
            createdAt: new Date(log.createdAt)
          }))
        )
      );
  }

  createPaymentLink(id: number, payload: CreateClientInvoicePaymentLinkDto) {
    return this.http
      .post<ClientInvoicePaymentLink>(
        `${environment.BACKEND}/doc/client-invoice/${id}/payment-link`,
        payload
      )
      .pipe(map((link) => this.normalizePaymentLink(link)));
  }

  getPaymentLinks(id: number) {
    return this.http
      .get<ClientInvoicePaymentLink[]>(`${environment.BACKEND}/doc/client-invoice/${id}/payment-links`)
      .pipe(map((links) => links.map((link) => this.normalizePaymentLink(link))));
  }

  deletePaymentLink(id: number, linkId: number) {
    return this.http
      .delete<ClientInvoicePaymentLink>(
        `${environment.BACKEND}/doc/client-invoice/${id}/payment-link/${linkId}`
      )
      .pipe(map((link) => this.normalizePaymentLink(link)));
  }

  delete(id: number) {
    return this.http.delete<void>(`${environment.BACKEND}/doc/client-invoice/${id}`);
  }

  getPdfUrl(id: number, type: ClientInvoicePrintType = 'article') {
    if (type === 'id') {
      return `${environment.BACKEND}/doc/client-invoice/pdf/${id}/by-id`;
    }
    return `${environment.BACKEND}/doc/client-invoice/pdf/${id}/by-article`;
  }

  getPdf(id: number, type: ClientInvoicePrintType = 'article') {
    return this.http.get(this.getPdfUrl(id, type), { responseType: 'blob' });
  }

  productsToPayload(products: ProductInDoc[]): InvoiceProductDto[] {
    return products.map((item) => ({
      product_id: item.product.id,
      quan: Number(item.quan),
      price: Number(item.price)
    }));
  }

  private normalizeListRow(row: ClientInvoiceListRow): ClientInvoiceListRow {
    return {
      ...row,
      date: new Date(row.date),
      date_created: new Date(row.date_created),
      total: Number(row.total),
      paid_total: row.paid_total !== undefined ? Number(row.paid_total) : undefined,
      returned_total: row.returned_total !== undefined ? Number(row.returned_total) : undefined,
      collectible_total:
        row.collectible_total !== undefined ? Number(row.collectible_total) : undefined,
      refunded_total: row.refunded_total !== undefined ? Number(row.refunded_total) : undefined,
      refund_pending_total:
        row.refund_pending_total !== undefined ? Number(row.refund_pending_total) : undefined,
      refund_due_total:
        row.refund_due_total !== undefined ? Number(row.refund_due_total) : undefined,
      has_returns: Boolean(row.has_returns)
    };
  }

  private normalizeInvoice(invoice: ClientInvoice): ClientInvoice {
    return {
      ...invoice,
      date: new Date(invoice.date),
      date_created: new Date(invoice.date_created),
      total: Number(invoice.total),
      paid_total: invoice.paid_total !== undefined ? Number(invoice.paid_total) : undefined,
      returned_total:
        invoice.returned_total !== undefined ? Number(invoice.returned_total) : undefined,
      collectible_total:
        invoice.collectible_total !== undefined ? Number(invoice.collectible_total) : undefined,
      refunded_total:
        invoice.refunded_total !== undefined ? Number(invoice.refunded_total) : undefined,
      refund_pending_total:
        invoice.refund_pending_total !== undefined ? Number(invoice.refund_pending_total) : undefined,
      refund_due_total:
        invoice.refund_due_total !== undefined ? Number(invoice.refund_due_total) : undefined,
      has_returns: Boolean(invoice.has_returns),
      items: (invoice.items || []).map((item) => ({
        ...item,
        quan: Number(item.quan),
        price: Number(item.price)
      }))
    };
  }

  private normalizePaymentLink(link: ClientInvoicePaymentLink): ClientInvoicePaymentLink {
    return {
      ...link,
      amount: Number(link.amount),
      expires_at: new Date(link.expires_at)
    };
  }
}
